import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { maxAPI } from '../services/api';
import { Users, ArrowLeft, Search, Bot } from 'lucide-react';

// Единая MAX-аудитория: дедуп по MAX user ID в пределах аккаунта. НЕ объединяет
// CRM-карточки. Маркетинговая доступность считается отдельно от «исторических».
const CRM_BADGE = {
  verified: ['badge-success', 'подтверждён'],
  conflict: ['badge-danger', 'конфликт'],
  candidate: ['badge-warning', 'кандидат'],
  unlinked: ['badge-muted', '—'],
};
function Crm({ s }) { const [c, l] = CRM_BADGE[s] || CRM_BADGE.unlinked; return <span className={`badge ${c}`}>{l}</span>; }

// Статус идентификации (verified — только при подтверждённом клиенте; неподтверждённое не выдаём за verified)
const ID_BADGE = {
  verified: ['badge-success', 'подтверждён'],
  phone_verified: ['badge-warning', 'телефон подтверждён'],
  candidate: ['badge-warning', 'кандидат'],
  conflict: ['badge-danger', 'конфликт'],
  unlinked: ['badge-muted', 'не связан'],
};
function IdBadge({ s }) { const [c, l] = ID_BADGE[s] || ID_BADGE.unlinked; return <span className={`badge ${c}`}>{l}</span>; }

function UserCard({ user, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
        <h2 className="modal-title"><Users size={18} /> MAX-пользователь {user.max_user_id}</h2>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <span>Идентификация:</span><IdBadge s={user.identity_status} />
          {user.has_conflict && <span className="badge badge-danger">конфликт CRM</span>}
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
          Первая активность: {user.first_seen_at || '—'} · последняя: {user.last_seen_at || '—'}<br />
          Телефон подтверждён: {user.phone_verified ? '✓' : '—'}
          {user.verified_method ? ` (${user.verified_method}, ${user.verified_at || ''})` : ''}<br />
          Подтверждённый клиент Segmira: {user.verified_client_id ? user.verified_client_id : '— (нет)'}<br />
          Предполагаемый CRM contact ID: {user.suspected_contact_id || '—'} <span style={{ fontStyle: 'italic' }}>(не доказательство личности)</span>
        </div>
        <h4>Подписки на боты ({user.subscription_count})</h4>
        <table className="table">
          <thead><tr><th>Бот</th><th>Статус</th><th>CRM</th><th>Первая</th><th>Последняя</th></tr></thead>
          <tbody>
            {user.bots.map((b, i) => (
              <tr key={i}><td>{b.title}</td><td><span className="badge badge-muted">{b.status}</span></td>
                <td><Crm s={b.crm_link_status} /></td><td>{b.first_seen_at || '—'}</td><td>{b.last_seen_at || '—'}</td></tr>
            ))}
          </tbody>
        </table>
        <h4 style={{ marginTop: 14 }}>CRM-кандидаты</h4>
        {user.crm_candidates.length === 0 ? <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>нет</p> : (
          <ul style={{ fontSize: 13 }}>
            {user.crm_candidates.map((c, i) => (
              <li key={i}>{c.external_system}: {c.external_contact_id} — <Crm s={c.link_status} /> <span style={{ color: 'var(--text-muted)' }}>(не подтверждён)</span></li>
            ))}
          </ul>
        )}
        <div style={{ textAlign: 'right', marginTop: 12 }}><button className="btn" onClick={onClose}>Закрыть</button></div>
      </div>
    </div>
  );
}

export default function MaxAudiencePage() {
  const navigate = useNavigate();
  const [m, setM] = useState(null);
  const [rows, setRows] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [q, setQ] = useState('');
  const [card, setCard] = useState(null);
  const [state, setState] = useState('loading');  // loading|ready|off|error

  const load = useCallback(async () => {
    setState('loading');
    try {
      const [au, me, cf] = await Promise.all([maxAPI.audience(), maxAPI.audienceMetrics(), maxAPI.crmConflicts()]);
      setRows(au.data || []); setM(me.data); setConflicts(cf.data || []); setState('ready');
    } catch (e) { setState(e.response?.status === 404 ? 'off' : 'error'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCard = async (uid) => { try { const { data } = await maxAPI.audienceUser(uid); setCard(data); } catch (e) { /* ignore */ } };
  const shown = rows.filter(r => !q || String(r.max_user_id).includes(q));

  return (
    <div className="page">
      <div className="page-header">
        <h1><Users size={22} /> Все MAX-пользователи</h1>
        <button className="btn" onClick={() => navigate('/bots')}><ArrowLeft size={16} /> К ботам</button>
      </div>

      {state === 'loading' && <div className="card" style={{ color: 'var(--text-muted)' }}>Загрузка…</div>}
      {state === 'off' && <div className="card">Модуль MAX выключен (feature flag).</div>}
      {state === 'error' && <div className="card" style={{ borderColor: 'var(--danger)' }}>
        <span style={{ color: 'var(--danger)' }}>Ошибка загрузки.</span>
        <button className="btn btn-sm" style={{ marginLeft: 12 }} onClick={load}>Повторить</button></div>}

      {state === 'ready' && m && (
        <>
          <div className="stat-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: 12, marginBottom: 8 }}>
            <div className="stat-card"><div className="stat-card-value">{m.unique_users}</div><div className="stat-card-label">Уникальных пользователей</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.total_subscriptions}</div><div className="stat-card-label">Подписок на ботов</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.users_in_multiple_bots}</div><div className="stat-card-label">В нескольких ботах</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.confirmed_active_subscriptions}</div><div className="stat-card-label">Подтверждённо активных</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.historical_imported_subscriptions}</div><div className="stat-card-label">Исторических (статус неизвестен)</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.stopped_subscriptions}</div><div className="stat-card-label">Остановили бота</div></div>
            <div className="stat-card"><div className="stat-card-value">{m.marketing_reachable}</div><div className="stat-card-label">Доступны для маркетинга</div></div>
          </div>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 0 }}>{m.marketing_reachable_note}</p>

          {conflicts.length > 0 && (
            <div className="card" style={{ borderColor: 'var(--warning)', marginBottom: 12 }}>
              <strong>⚠ Конфликтные CRM contact ID: {conflicts.length}</strong>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {conflicts.slice(0, 20).map(c => `${c.external_contact_id} → ${c.max_user_ids.join(', ')}`).join(' · ')}
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '8px 0' }}>
            <Search size={15} />
            <input className="form-input" style={{ maxWidth: 280 }} placeholder="Поиск по MAX user ID"
              value={q} onChange={e => setQ(e.target.value.trim())} />
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{shown.length} из {rows.length}</span>
          </div>

          <table className="table">
            <thead><tr><th>MAX user ID</th><th>Подписок</th><th>Боты</th><th>CRM</th><th>Первая</th><th>Последняя</th></tr></thead>
            <tbody>
              {shown.slice(0, 500).map(u => (
                <tr key={u.max_user_id} style={{ cursor: 'pointer' }} onClick={() => openCard(u.max_user_id)}>
                  <td style={{ fontFamily: 'monospace' }}>{u.max_user_id}</td>
                  <td>{u.subscription_count}</td>
                  <td style={{ fontSize: 12 }}><Bot size={12} /> {u.bots.map(b => `${b.title}(${b.status})`).join(', ')}</td>
                  <td><Crm s={u.crm_status} /></td>
                  <td>{u.first_seen_at || '—'}</td>
                  <td>{u.last_seen_at || '—'}</td>
                </tr>
              ))}
              {shown.length === 0 && <tr><td colSpan={6} style={{ color: 'var(--text-muted)' }}>Ничего не найдено</td></tr>}
            </tbody>
          </table>
          {shown.length > 500 && <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>Показаны первые 500 — уточните поиск.</p>}
        </>
      )}

      {card && <UserCard user={card} onClose={() => setCard(null)} />}
    </div>
  );
}
